<script setup lang="ts">
import NavigationMenuListItem from './NavigationMenuListItem.vue'

const route = useRoute()
const gtm = useGTM()

const storyblokStore = useStoryblokStore()

const { state: storyblok } = storeToRefs(storyblokStore)
const isSearchExpanded = ref(false)
const isLogoCollapsed = ref(false)
const currentTrigger = ref('')

const { links: headerNavbarLinks } = useHeaderNavbarItems()
const { categoryLinks } = useCategoryLinks(headerNavbarLinks)
const { disableRegularNewsletter } = useSegment()
const { isAddressOrPaymentStep } = useLogoLink()

function setSearchExpanded() {
  isSearchExpanded.value = true
  isLogoCollapsed.value = true
}

function setLogoCollapsed() {
  isLogoCollapsed.value = true
}

function onMenuClick(menuItemLink: string) {
  gtm.pushMenuClick({
    menuItemLink,
    navigationType: 'desktop',
    menuType: 'main',
  })

  navigateTo(menuItemLink)
}
</script>

<template>
  <div
    class="navigation-container"
    :class="{ 'navigation-container--centered': isAddressOrPaymentStep }"
  >
    <NavigationMenuRoot
      v-model="currentTrigger"
      class="NavigationMenuRoot"
      :class="{ 'NavigationMenuRoot--centered': isAddressOrPaymentStep }"
      data-test="navbar"
    >
      <NavigationMenuList
        class="NavigationMenuList"
        :class="{ 'NavigationMenuList--centered': isAddressOrPaymentStep }"
      >
        <NavigationMenuItem
          :as-child="true"
          class="NavigationMenuIcon"
        >
          <HeaderLogo
            :is-logo-collapsed="isLogoCollapsed"
            @logo-collapse="setLogoCollapsed"
            @click="onMenuClick($url('/'))"
          />
        </NavigationMenuItem>
        <template v-if="!isSearchExpanded && !isAddressOrPaymentStep">
          <NavigationMenuItem
            v-for="(headerNavbarLink) in headerNavbarLinks"
            :key="headerNavbarLink._uid"
          >
            <NavigationMenuTrigger
              class="NavigationMenuTrigger"
              @mousedown.left="onMenuClick($url(headerNavbarLink.url))"
            >
              {{ headerNavbarLink.text }}
              <div
                v-bind="{
                  ...(route.path.startsWith($url(headerNavbarLink.url)) && { 'data-active': true }),
                }"
                class="WavyLine"
              />
            </NavigationMenuTrigger>
            <NavigationMenuContent
              class="NavigationMenuContent"
            >
              <template
                v-for="submenu in categoryLinks?.find(category => category.id === headerNavbarLink._uid)?.submenus"
                :key="submenu.name"
              >
                <div>
                  <p class="ListHeading">
                    {{ submenu.name }}
                  </p>
                  <ul
                    class="List"
                  >
                    <NavigationMenuListItem
                      v-for="submenuLink in submenu.links.filter(submenu => !submenu.disabled)"
                      :key="submenuLink.url"
                      :href="$url(submenuLink.url)"
                      :title="submenuLink.text"
                      :highlighted="submenuLink.isHighlighted"
                    />
                  </ul>
                </div>
              </template>
              <div class="navbar-bottom">
                <HeaderCountryAndSocial />
                <Newsletter
                  v-if="storyblok.newsletter && !disableRegularNewsletter"
                  :newsletter="storyblok.newsletter"
                  background-color="var(--pink)"
                  context="menu"
                />
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </template>
      </NavigationMenuList>
      <div class="ViewportPosition">
        <NavigationMenuViewport
          class="NavigationMenuViewport"
        />
      </div>
    </NavigationMenuRoot>
    <div
      v-if="!isAddressOrPaymentStep"
      class="search-container"
      :class="{ expanded: isSearchExpanded }"
    >
      <HeaderSearch
        :expanded="isSearchExpanded"
        @search-expand="setSearchExpanded()"
        @search-collapse="isSearchExpanded = false"
      />
      <HeaderCartLink data-accessibility="cart" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';

.navigation-container {
  display: flex;
  margin-top: 1.5rem;
  margin-left: 2rem;
  margin-right: 3.4rem;
  align-items: center;
  justify-content: space-between;

  &--centered {
    background-color: var(--gray);
    justify-content: center;
  }

  @media (max-width: $tablet) {
    display: none;
  }

  @media screen and (max-width: $desktop) {
    margin-left: 1rem;
    margin-right: 1rem;

    &--centered {
      margin-left: 0;
      margin-right: 0;
      padding: 1.5rem;
    }
  }
}

.NavigationMenuRoot--centered {
  justify-content: center;
}

.NavigationMenuList--centered {
  background-color: transparent;
  border-radius: 0;
}

.search-container {
  display: flex;
  flex-grow: 1;
  margin-left: 1rem;
  align-items: center;
  width: 40%;
  transition: width 0.5s ease-out;

  &.expanded {
    width: 80%;
  }
}

.navbar-bottom {
  grid-column: span 3;
}
</style>
